import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Settings,
  BookOpen,
  Package,
  BarChart3,
  GraduationCap,
  Shield,
  FileText,
  MessageSquare,
  Download,
} from "lucide-react";
import {
  AdminProvider,
  useAdmin,
  AdminTable,
  AdminOverview,
  AdminModal,
} from "@/components/admin";

const tabs = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "pillars", label: "Pillars", icon: Shield },
  { id: "protocols", label: "Protocols", icon: BookOpen },
  { id: "toolkits", label: "Toolkits", icon: Package },
  { id: "data-guides", label: "Data Guides", icon: BarChart3 },
  { id: "training", label: "Training", icon: GraduationCap },
  { id: "assets", label: "Assets", icon: FileText },
  { id: "contacts", label: "Contacts", icon: MessageSquare },
];

function AdminContent() {
  const { data, loading, fetchAllData } = useAdmin();
  const [activeTab, setActiveTab] = React.useState("overview");

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
        </div>
      );
    }

    switch (activeTab) {
      case "overview":
        return <AdminOverview />;
      case "pillars":
        return (
          <AdminTable
            type="pillars"
            items={data.pillars}
            columns={[
              { key: "title", label: "Title" },
              { key: "order", label: "Order" },
              { key: "slug", label: "Slug" },
            ]}
          />
        );
      case "protocols":
        return (
          <AdminTable
            type="protocols"
            items={data.protocols}
            columns={[
              { key: "title", label: "Title" },
              {
                key: "urgency",
                label: "Urgency",
                render: (v) => (
                  <span className={`badge-urgency-${v} px-2 py-1 rounded-full text-xs uppercase`}>
                    {v}
                  </span>
                ),
              },
              { key: "view_count", label: "Views" },
            ]}
          />
        );
      case "toolkits":
        return (
          <AdminTable
            type="toolkits"
            items={data.toolkits}
            columns={[
              { key: "title", label: "Title" },
              { key: "download_count", label: "Downloads" },
            ]}
          />
        );
      case "data-guides":
        return (
          <AdminTable
            type="data-guides"
            items={data.dataGuides}
            columns={[
              { key: "title", label: "Title" },
              { key: "domain", label: "Domain" },
              { key: "view_count", label: "Views" },
            ]}
          />
        );
      case "training":
        return (
          <AdminTable
            type="training"
            items={data.training}
            columns={[
              { key: "title", label: "Title" },
              { key: "time_estimate", label: "Duration" },
              { key: "view_count", label: "Views" },
            ]}
          />
        );
      case "assets":
        return (
          <AdminTable
            type="assets"
            items={data.assets}
            columns={[
              { key: "title", label: "Title" },
              {
                key: "file_type",
                label: "Type",
                render: (v) => (
                  <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded uppercase">
                    {v}
                  </span>
                ),
              },
              {
                key: "file_url",
                label: "File",
                render: (v, item) =>
                  v ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${v}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                      data-testid={`download-asset-${item.id}`}
                    >
                      <Download className="w-3 h-3" /> Download
                    </a>
                  ) : (
                    <span className="text-slate-400 text-xs">No file</span>
                  ),
              },
              { key: "download_count", label: "Downloads" },
            ]}
          />
        );
      case "contacts":
        return (
          <AdminTable
            type="contacts"
            items={data.contacts}
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "service_interest", label: "Interest" },
              {
                key: "status",
                label: "Status",
                render: (v) => (
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      v === "new" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {v}
                  </span>
                ),
              },
            ]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="admin-sidebar hidden lg:block">
          <div className="flex items-center gap-2 mb-8">
            <Settings className="w-6 h-6" />
            <span className="font-bold text-lg">Admin Panel</span>
          </div>
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`admin-nav-item w-full ${activeTab === tab.id ? "active" : ""}`}
                  data-testid={`admin-tab-${tab.id}`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile tabs */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 overflow-x-auto">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-16 py-3 px-2 flex flex-col items-center gap-1 text-xs ${
                    activeTab === tab.id ? "text-orange-500" : "text-slate-500"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-24 lg:pb-8">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {renderContent()}
          </motion.div>
        </main>
      </div>

      <AdminModal />
    </div>
  );
}

export default function Admin() {
  return (
    <AdminProvider>
      <AdminContent />
    </AdminProvider>
  );
}
